import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Grid, Paper, Stack, Avatar, Chip, CircularProgress, Tooltip } from '@mui/material';
import { Description, CalendarToday, AccessTime, Business, Person, WorkHistory, Timer, Info } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import { getStatusColor, priorityColors } from "../../../utils/statusColor";
import { formatDate } from '../../../utils/dateFormat';

const InfoCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2.5),
    borderRadius: '16px',
    backgroundColor: '#fff',
    border: '1px solid #f1f5f9',
    boxShadow: 'none',
}));

const TeamSection = styled(Box)(({ theme }) => ({
    backgroundColor: '#f8fafc',
    borderRadius: '16px',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const ProjectViewPage = ({ project }) => {
    const [loading, setLoading] = useState(true);
    const [projectDetails, setProjectDetails] = useState(null);
    const isFetching = useRef(false);
    const lastFetchedId = useRef(null);

    useEffect(() => {
        const fetchProjectDetails = async () => {
            // 1. Guard against missing ID or redundant calls for the same ID
            if (!project.id || project.id === lastFetchedId.current) return;

            // 2. Prevent concurrent duplicate requests
            if (isFetching.current) return;

            isFetching.current = true;
            setLoading(true);

            try {
                const response = await getData(API_ENDPOINTS.fetchProjectById(project.id));
                const resData = response.data || response;

                if (resData.status === 'SUCCESS') {
                    const data = resData.message.data;
                    setProjectDetails(data);
                    lastFetchedId.current = project.id;
                }
            } catch (error) {
                return null;
            } finally {
                setLoading(false);
                isFetching.current = false;
            }
        };

        fetchProjectDetails();
    }, [project.id]);

    if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;

    return (
        <Box>
            <Grid container spacing={4}>
                <Grid size={{ xs: 12, md: 5 }}>
                    <Stack spacing={3}>
                        <Box>
                            <Typography variant="overline" color="text.secondary" fontWeight="700" sx={{ letterSpacing: 1 }}>
                                CORE INFORMATION
                            </Typography>
                            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6', borderRadius: '8px' }}>
                                    <Description fontSize="small" />
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="700" color="#475569">Description</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mt: 0.5 }}>{projectDetails?.project?.description}</Typography>
                                </Box>
                            </Stack>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="caption" fontWeight="700" color="text.secondary">Priority</Typography>
                                <Chip
                                    label={projectDetails?.project?.priority}
                                    size="small"
                                    sx={{
                                        mt: 1,
                                        display: 'flex',
                                        fontWeight: 600,
                                        width: 'fit-content',
                                        px: 1,
                                        bgcolor: priorityColors[projectDetails?.project?.priority]?.bg,
                                        color: priorityColors[projectDetails?.project?.priority]?.color,
                                        border: priorityColors[projectDetails?.project?.priority]?.border || '1px solid #cbd5e1',
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="caption" fontWeight="700" color="text.secondary">Status</Typography>
                                <Chip
                                    label={projectDetails?.project?.status}
                                    size="small"
                                    sx={{
                                        mt: 1,
                                        display: 'flex',
                                        fontWeight: 600,
                                        width: 'fit-content',
                                        px: 1,
                                        bgcolor: getStatusColor(projectDetails?.project?.status).bg,
                                        color: getStatusColor(projectDetails?.project?.status).color,
                                        border: getStatusColor(projectDetails?.project?.status).border || '1px solid #cbd5e1',
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Avatar sx={{ bgcolor: 'transparent', color: '#3b82f6', border: '1px solid #e2e8f0', width: 36, height: 36 }}>
                                        <CalendarToday fontSize="inherit" />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700 }}>Start Date</Typography>
                                        <Typography variant="body2" fontWeight="700">{formatDate(projectDetails?.project?.start_date)}</Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Avatar sx={{ bgcolor: 'transparent', color: '#ef4444', border: '1px solid #e2e8f0', width: 36, height: 36 }}>
                                        <AccessTime fontSize="inherit" />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700 }}>Deadline</Typography>
                                        <Typography variant="body2" fontWeight="700">{formatDate(projectDetails?.project?.end_date)}</Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                            {/* Working Days with Tooltip */}
                            <Grid size={{ xs: 6 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Avatar sx={{ bgcolor: 'transparent', color: '#6366f1', border: '1px solid #e2e8f0', width: 36, height: 36 }}>
                                        <WorkHistory fontSize="inherit" />
                                    </Avatar>
                                    <Box>
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700 }}>
                                                Working Days
                                            </Typography>
                                            <Tooltip title={`Calculation: ${projectDetails?.project?.total_working_days || 0} Working Days × 8 Hours/Day`} arrow placement="top">
                                                <Info sx={{ fontSize: 12, color: 'text.disabled', cursor: 'help' }} />
                                            </Tooltip>
                                        </Stack>
                                        <Typography variant="body2" fontWeight="700">
                                            {projectDetails?.project?.total_working_days || 0} Days
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Grid>

                            {/* Total Hours with Tooltip */}
                            <Grid size={{ xs: 6 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Avatar sx={{ bgcolor: 'transparent', color: '#f59e0b', border: '1px solid #e2e8f0', width: 36, height: 36 }}>
                                        <Timer fontSize="inherit" />
                                    </Avatar>
                                    <Box>
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700 }}>
                                                Total Hours
                                            </Typography>
                                            <Tooltip title={`Total Hours = Working Days (${projectDetails?.project?.total_working_days || 0}) * 8`} arrow placement="top">
                                                <Info sx={{ fontSize: 12, color: 'text.disabled', cursor: 'help' }} />
                                            </Tooltip>
                                        </Stack>
                                        <Typography variant="body2" fontWeight="700">
                                            {projectDetails?.project?.total_working_hours || 0} Hrs
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                        </Grid>

                        <Box sx={{ background: '#FFFFFF', py: 2 }}>
                            <Box sx={{ mt: 1, background: '#FFFFFF' }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="caption" fontWeight="700" color="text.secondary">
                                        OVERALL PROGRESS
                                    </Typography>
                                    <Typography variant="caption" fontWeight="700" color="#2563eb">
                                        {projectDetails?.project?.progress || 0}%
                                    </Typography>
                                </Stack>

                                <Box sx={{
                                    width: '100%',
                                    height: 8,
                                    bgcolor: '#f1f5f9',
                                    borderRadius: 4,
                                    overflow: 'hidden'
                                }}>
                                    <Box
                                        sx={{
                                            height: '100%',
                                            width: `${projectDetails?.project?.progress || 0}%`,
                                            bgcolor: (projectDetails?.project?.progress || 0) >= 80 ? '#22c55e' : '#2563eb',
                                            transition: 'width 0.5s ease-in-out',
                                            borderRadius: 4
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="overline" color="text.secondary" fontWeight="700">ASSOCIATED CLIENTS</Typography>
                            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {projectDetails?.clients?.map((client) => (
                                    <Chip
                                        key={client.id}
                                        icon={<Business fontSize="small" />}
                                        label={`${client.first_name} ${client.last_name}`.trim()}
                                        variant="outlined"
                                        sx={{
                                            borderRadius: '8px',
                                            p: 1,
                                            height: '40px',
                                            fontWeight: 600,
                                            color: '#475569'
                                        }}
                                    />
                                ))}
                                {/* Fallback if array is empty */}
                                {(!projectDetails?.clients || projectDetails?.clients.length === 0) && (
                                    <Typography variant="body2" color="text.disabled">No clients associated</Typography>
                                )}
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="overline" color="text.secondary" fontWeight="700">
                                KEY STAKEHOLDERS
                            </Typography>
                            <Stack spacing={2} sx={{ mt: 1 }}>
                                {[
                                    {
                                        data: projectDetails?.manager,
                                        role: 'PROJECT MANAGER',
                                        color: '#3b82f6'
                                    },
                                    {
                                        data: projectDetails?.team_leader,
                                        role: 'TEAM LEAD',
                                        color: '#10b981'
                                    }
                                ].map((stakeholder, idx) => {
                                    const person = stakeholder.data;
                                    if (!person) return null; // Safety check

                                    const fullName = `${person.first_name} ${person.last_name}`.trim();
                                    const initials = (person.first_name?.[0] || '') + (person.last_name?.[0] || '');

                                    return (
                                        <InfoCard key={idx} sx={{ py: 1.5 }}>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Avatar
                                                    sx={{
                                                        bgcolor: `${stakeholder.color}20`,
                                                        color: stakeholder.color,
                                                        fontWeight: 700,
                                                        fontSize: '0.9rem',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    {initials || '?'}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight="700" sx={{ textTransform: 'capitalize' }}>
                                                        {fullName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                        {stakeholder.role}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </InfoCard>
                                    );
                                })}
                            </Stack>
                        </Box>
                    </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 7 }} sx={{ bgcolor: '#ffffff', border: '1px solid #e0e0e0', borderRadius: '8px', p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="overline" color="text.secondary" fontWeight="700">DEPARTMENTS & WORKSPACE</Typography>
                    </Stack>

                    {/* Dynamic Department Mapping */}
                    {projectDetails?.departments?.map((dept) => (
                        <Box key={dept.department_id} sx={{ mb: 4 }}>
                            <Typography variant="h6" fontWeight="800" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box sx={{ width: 4, height: 24, bgcolor: '#2563eb', mr: 1.5, borderRadius: 1 }} />
                                {dept.department_name}
                                <Typography component="span" variant="body2" sx={{ ml: 1, color: 'text.secondary', fontWeight: 500 }}>
                                    ({dept.teams?.length || 0} {dept.teams?.length === 1 ? 'Team' : 'Teams'})
                                </Typography>
                            </Typography>

                            {/* Dynamic Team Mapping within the Department */}
                            {dept.teams?.map((team) => (
                                <TeamSection key={team.team_id}>
                                    <Typography variant="subtitle2" color="#475569" fontWeight="700" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Person sx={{ fontSize: 18, mr: 1, opacity: 0.6 }} /> {team.team_name}
                                    </Typography>

                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {team.members?.map((member) => {
                                            const memberName = `${member.first_name} ${member.last_name}`.trim();
                                            const initials = (member.first_name?.[0] || '') + (member.last_name?.[0] || '');

                                            return (
                                                <Chip
                                                    key={member.id}
                                                    avatar={
                                                        <Avatar sx={{ fontSize: '0.6rem !important', textTransform: 'uppercase', }}>
                                                            {initials}
                                                        </Avatar>
                                                    }
                                                    label={memberName}
                                                    sx={{
                                                        bgcolor: '#fff',
                                                        border: '1px solid #e2e8f0',
                                                        fontWeight: 600,
                                                        color: '#475569',
                                                        textTransform: 'capitalize'
                                                    }}
                                                />
                                            );
                                        })}
                                    </Stack>
                                </TeamSection>
                            ))}
                        </Box>
                    ))}
                </Grid>
            </Grid>
        </Box>
    );
};

export default ProjectViewPage;